<script setup lang="ts">
/**
 * ArticleImage
 *
 * Renders a real cover image when one is available and loads successfully,
 * otherwise falls back to a deterministic AbstractImage. The fallback also
 * kicks in when the image *field is set but the file fails to load* (404,
 * network error) — something a plain `v-if` on the field cannot detect.
 */
import type { AbstractVariant } from '~/composables/useAbstractImage'

const props = defineProps({
  /** Cover image URL; when empty or it fails to load, the abstract shows. */
  src: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    default: '',
  },
  /** Seed for the abstract fallback (e.g. the article title). */
  seed: {
    type: String,
    default: '',
  },
  /** Hex anchor for the abstract palette (e.g. category color). */
  baseColor: {
    type: String,
    default: '',
  },
  variant: {
    type: String as () => AbstractVariant,
    default: 'mesh',
  },
  /** Display size; numbers become px. Defaults fill the parent. */
  width: {
    type: [Number, String],
    default: '100%',
  },
  height: {
    type: [Number, String],
    default: '100%',
  },
  /** Intrinsic size hints for NuxtImg optimization. */
  fetchWidth: {
    type: Number,
    default: 600,
  },
  fetchHeight: {
    type: Number,
    default: 400,
  },
  eager: {
    type: Boolean,
    default: false,
  },
  /** Vertical placement for overlay/slot content on the abstract fallback. */
  overlayAlign: {
    type: String as () => 'center' | 'end' | 'start',
    default: 'center',
  },
  /**
   * When set, the box uses this CSS aspect-ratio (e.g. '16 / 9') with height:auto
   * instead of a fixed height — so a `cover` image whose ratio matches isn't cropped.
   * Used for the article hero; cards keep their fixed-height crop for uniform grids.
   */
  aspectRatio: {
    type: [Number, String],
    default: '',
  },
})

const errored = ref(false)
watch(
  () => props.src,
  () => {
    errored.value = false
  }
)

const showImage = computed(() => !!props.src && !errored.value)

const overlayJustify = computed(() => ({ center: 'center', start: 'flex-start', end: 'flex-end' })[props.overlayAlign])

const sizeStyle = computed(() => {
  const width = typeof props.width === 'number' ? `${props.width}px` : props.width
  if (props.aspectRatio) {
    return { width, aspectRatio: String(props.aspectRatio), height: 'auto' }
  }
  return {
    width,
    height: typeof props.height === 'number' ? `${props.height}px` : props.height,
  }
})
</script>

<template>
  <div class="article-image" :style="sizeStyle">
    <NuxtImg
      v-if="showImage"
      :src="src"
      :alt="alt"
      :width="fetchWidth"
      :height="fetchHeight"
      fit="cover"
      format="webp"
      :loading="eager ? 'eager' : 'lazy'"
      class="article-image__img"
      @error="errored = true"
    />
    <AbstractImage v-else :seed="seed" :base-color="baseColor" :variant="variant" width="100%" height="100%" />

    <!-- Overlay (e.g. a category chip) drawn over either the image or the abstract. -->
    <div v-if="$slots.default" class="article-image__overlay" :style="{ justifyContent: overlayJustify }">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.article-image {
  /* Anchors the absolutely-positioned overlay below. Without it the overlay
     resolves against the nearest positioned ancestor — the v-card — so an
     `end`-aligned chip drops to the bottom of the whole card and lands on top
     of the card actions instead of sitting over the image. */
  position: relative;
  overflow: hidden;
}

.article-image__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.article-image__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
}
</style>
